import * as React from 'react'

import { clsx } from 'clsx'

import { Text, Flex, ChannelIcon } from '@buffer-mono/popcorn'

import { PostMediaAsset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'
import { getPostDisplayText } from '~publish/pages/Community/PostMiniature/utils'

import sharedBadgeStyles from '../../../shared/CarouselBadge.module.css'
import { CommentMetrics } from '../CommentMetrics'

import styles from './PostListItemCard.module.css'

import type { JSX } from 'react'

type PostListItemCardProps = {
  post: CommentsPostCard_PostData
  isMultiChannel: boolean
  /** When true, card is grayed out and metrics are hidden until hover/selected */
  isAllAddressed?: boolean
  supportsComments?: boolean
}

export function PostListItemCard({
  post,
  isMultiChannel,
  isAllAddressed = false,
  supportsComments = true,
}: PostListItemCardProps): JSX.Element {
  const [isHovered, setIsHovered] = React.useState(false)
  const displayText = getPostDisplayText(post)
  const hasText = Boolean(displayText)
  const hasAssets = Boolean(post.assets?.length)

  return (
    <article
      className={clsx(
        styles.postContent,
        (isAllAddressed || !supportsComments) && styles.postContentAddressed,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex align="start" gap="sm" className={styles.postBody}>
        <Flex direction="column" gap="2xs" className={styles.textContent}>
          {isMultiChannel && (
            <PostHeader
              channelService={post.channelService}
              channelName={post.channel?.displayName ?? post.channel?.name}
            />
          )}
          {hasText ? (
            <Text
              weight="regular"
              lineHeight="tight"
              className={styles.postText}
            >
              {displayText}
            </Text>
          ) : (
            <Text
              weight="regular"
              lineHeight="tight"
              color="subtle"
              className={styles.postText}
            >
              <em>This post has no caption</em>
            </Text>
          )}
        </Flex>
        {hasAssets && post.assets && <MediaThumbnail assets={post.assets} />}
      </Flex>
      {supportsComments ? (
        <CommentMetrics postId={post.id} variant="list" isActive={isHovered} />
      ) : (
        <Text size="sm" color="subtle">
          <em>Comments not available</em>
        </Text>
      )}
    </article>
  )
}

type PostHeaderProps = {
  channelService: CommentsPostCard_PostData['channelService']
  channelName?: string | null
}

function PostHeader({
  channelService,
  channelName,
}: PostHeaderProps): JSX.Element {
  return (
    <Flex align="center" gap="2xs" className={styles.postHeader}>
      <span
        className={styles.channelIcon}
        data-channel={channelService}
        aria-label={`${channelService} channel`}
      >
        {isChannelIconService(channelService) && (
          <ChannelIcon type={channelService} size={10} />
        )}
      </span>
      <Text size="sm" weight="medium" className={styles.channelName}>
        {channelName}
      </Text>
    </Flex>
  )
}

type MediaThumbnailProps = {
  assets: NonNullable<CommentsPostCard_PostData['assets']>
}

function MediaThumbnail({ assets }: MediaThumbnailProps): JSX.Element {
  const hasMultipleAssets = assets.length > 1
  const remainingCount = assets.length - 1

  return (
    <div className={styles.mediaContainer}>
      <PostMediaAsset asset={assets[0]} className={styles.assets} />
      {hasMultipleAssets && (
        <CarouselBadge count={remainingCount} size="small" />
      )}
    </div>
  )
}

type CarouselBadgeProps = {
  count: number
  size: 'small' | 'medium'
}

function CarouselBadge({ count, size }: CarouselBadgeProps): JSX.Element {
  const badgeClasses = clsx(
    sharedBadgeStyles.carouselBadge,
    sharedBadgeStyles[size],
  )

  return <span className={badgeClasses}>{`+${count}`}</span>
}
